import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ScrollText, ShieldCheck } from 'lucide-react';
import { useApi } from '../lib/useApi';
import { actionLabel, dateTime, num } from '../lib/format';
import { Button, Card, Chip, Empty, ErrorBanner, PageHeader, PageSkeleton, SearchBox, Segmented, Select } from '../components/ui';

interface Item {
  id: number; action: string; resource_type: string | null; details: string | null; ip_address: string | null; created_at: string;
  tenant_id: string | null; tenant: string | null; actor: string | null; actor_email: string | null; by_platform: boolean | null;
}
interface Resp { items: Item[]; total: number; page: number; page_size: number; actions: { action: string; n: number }[] }

function prettyDetails(details: string | null) {
  if (!details) return null;
  if (details.startsWith('{')) {
    try {
      const o = JSON.parse(details);
      return Object.entries(o).filter(([, v]) => typeof v !== 'object').map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ');
    } catch { /* plain text */ }
  }
  return details;
}

export default function AuditLog() {
  const navigate = useNavigate();
  const [scope, setScope] = useState<'all' | 'platform' | 'tenants'>('platform');
  const [days, setDays] = useState('30');
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [actorQ, setActorQ] = useState('');
  const [page, setPage] = useState(1);
  useEffect(() => { const id = window.setTimeout(() => setActorQ(actor), 350); return () => window.clearTimeout(id); }, [actor]);
  useEffect(() => { setPage(1); }, [scope, days, action, actorQ]);

  const qs = new URLSearchParams({ scope, days, page: String(page), page_size: '50', ...(action ? { action } : {}), ...(actorQ ? { actor: actorQ } : {}) });
  const { data, error, loading, reload } = useApi<Resp>(`/audit?${qs.toString()}`);

  if (loading && !data) return <PageSkeleton />;
  if (error) return <ErrorBanner text={error} onRetry={reload} />;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div>
      <PageHeader title="Audit log" subtitle="Who did what, across the platform and inside every tenant" />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Segmented value={scope} onChange={setScope} options={[{ key: 'platform', label: 'Platform team' }, { key: 'tenants', label: 'Tenant users' }, { key: 'all', label: 'Everything' }]} />
        <Select value={days} onChange={e => setDays(e.target.value)} className="!w-36">
          <option value="1">Last 24 hours</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="365">Last year</option>
        </Select>
        <Select value={action} onChange={e => setAction(e.target.value)} className="!w-56">
          <option value="">All actions</option>
          {(data?.actions || []).map(a => <option key={a.action} value={a.action}>{actionLabel(a.action)} ({a.n})</option>)}
        </Select>
        <div className="ml-auto"><SearchBox value={actor} onChange={setActor} placeholder="Filter by person or email" /></div>
      </div>
      <Card bodyClass="p-0">
        {!data || data.items.length === 0 ? <Empty icon={ScrollText} title="Nothing recorded for these filters" /> : (
          <>
            <table className="min-w-full">
              <thead className="border-b border-slate-100 bg-slate-50/60"><tr>
                {['When', 'Who', 'Action', 'Tenant', 'Details'].map(h => <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-slate-500">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map(i => (
                  <tr key={i.id} className="align-top">
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">{dateTime(i.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900">{i.actor || 'System'}{i.by_platform && <span title="Platform team"><ShieldCheck className="h-3.5 w-3.5 text-blue-600" /></span>}</div>
                      <div className="text-xs text-slate-500">{i.actor_email}</div>
                    </td>
                    <td className="px-4 py-3"><Chip tone={i.action.startsWith('tenant.delete') || i.action.includes('quarantine') ? 'red' : i.by_platform ? 'blue' : 'slate'}>{actionLabel(i.action)}</Chip></td>
                    <td className="px-4 py-3 text-sm">{i.tenant ? <button onClick={() => navigate(`/tenants/${i.tenant_id}`)} className="text-blue-700 hover:underline">{i.tenant}</button> : <span className="text-slate-400">{i.tenant_id ? 'Deleted tenant' : 'Platform'}</span>}</td>
                    <td className="max-w-md px-4 py-3 text-sm text-slate-600"><span className="line-clamp-2" title={i.details || ''}>{prettyDetails(i.details) || '—'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
              <span>{num(data.total)} events</span>
              <div className="flex items-center gap-2">
                <Button size="sm" icon={ChevronLeft} disabled={page <= 1} onClick={() => setPage(p => p - 1)} aria-label="Previous page" />
                <span>Page {page} of {pages}</span>
                <Button size="sm" icon={ChevronRight} disabled={page >= pages} onClick={() => setPage(p => p + 1)} aria-label="Next page" />
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
